import { useState, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AlertCircle, Loader2 } from "lucide-react";
import type { AHTPlannedAppointment } from "@/lib/pharmacyFirst/utils/generateAHTAppointments";

interface AHTTreatmentPlanConfirmModalProps {
  open: boolean;
  isDarkMode?: boolean;
  patientName: string;
  patientId: string;
  episodeOrigin: "initialSupply" | "transferIn";
  drugName: string;
  pgdId: string;
  initialDose: string;
  baselineWeightKg: number;
  baselineBmi: number;
  plannedAppointments: AHTPlannedAppointment[];
  patientEmail: string | null;
  onUpdatePatientEmail?: (email: string) => Promise<void>;
  onConfirm: () => void | Promise<void>;
  onCancel: () => void;
}

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
] as const;

function formatDate(isoDate: string): string {
  const date = new Date(isoDate + "T00:00:00Z");
  const day = date.getUTCDate();
  const month = MONTHS[date.getUTCMonth()];
  const year = date.getUTCFullYear();
  if (month === undefined) return isoDate;
  return `${day} ${month} ${year}`;
}

function SummaryRow({
  label,
  value,
  valueClassName,
  muted,
}: {
  label: string;
  value: string;
  valueClassName?: string;
  muted?: string;
}) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-1.5">
      <span className="text-sm text-gray-500 dark:text-gray-400 shrink-0">{label}</span>
      <span className={`text-sm text-right ${valueClassName || ""}`}>
        {value}
        {muted !== undefined && (
          <span className="ml-1.5 text-xs text-gray-500 dark:text-gray-400">{muted}</span>
        )}
      </span>
    </div>
  );
}

function AppointmentRow({ appointment }: { appointment: AHTPlannedAppointment }) {
  return (
    <div className="py-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold">{formatDate(appointment.date)}</p>
          <p className="text-sm mt-0.5">{appointment.serviceType}</p>
          <div className="flex flex-wrap gap-1.5 mt-1.5">
            {appointment.requiresVideoOrF2F && (
              <span className="inline-flex items-center rounded-full border border-transparent bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300 px-2 py-0.5 text-xs font-semibold">
                Video / F2F required
              </span>
            )}
            {appointment.isTitrationAppointment && (
              <span className="inline-flex items-center rounded-full border border-transparent bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 px-2 py-0.5 text-xs font-semibold">
                Titration
              </span>
            )}
          </div>
        </div>
        {/* <p className="text-xs text-gray-500 dark:text-gray-400 shrink-0 pt-0.5">
          Questionnaire sent {appointment.preConsultationSendValue} days before
        </p> */}
      </div>
    </div>
  );
}

export function AHTTreatmentPlanConfirmModal({
  open,
  isDarkMode = false,
  patientName,
  episodeOrigin,
  drugName,
  pgdId,
  initialDose,
  baselineWeightKg,
  baselineBmi,
  plannedAppointments,
  patientEmail,
  onUpdatePatientEmail,
  onConfirm,
  onCancel,
}: AHTTreatmentPlanConfirmModalProps) {
  const [isConfirming, setIsConfirming] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [emailInput, setEmailInput] = useState("");

  const emailMissing = !patientEmail;
  const emailValid = useMemo(() => {
    if (!emailMissing) return true;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailInput);
  }, [emailMissing, emailInput]);

  const canConfirm = !isConfirming && emailValid;

  async function handleConfirm() {
    setIsConfirming(true);
    setSaveError(null);
    try {
      if (emailMissing && emailInput) {
        await onUpdatePatientEmail?.(emailInput);
      }
      await onConfirm();
    } catch (error) {
      // The plan was not saved — stay open so it can be retried, and never
      // let the caller fall through to its "created" state.
      console.error("Failed to create AHT treatment plan:", error);
      setSaveError(
        "The treatment plan could not be saved. No plan has been created — please try again.",
      );
    } finally {
      setIsConfirming(false);
    }
  }

  const episodeLabel = episodeOrigin === "initialSupply" ? "Initial supply" : "Transfer in";

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen && !isConfirming) onCancel(); }}>
      <DialogContent
        className={`max-w-xl w-full ${isDarkMode ? "dark" : ""}`}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>Create treatment plan</DialogTitle>
          <p className="text-sm text-gray-500 dark:text-gray-400">{patientName}</p>
        </DialogHeader>

        <div className="flex flex-col gap-4 max-h-[70vh] overflow-y-auto pr-1">
          {/* Episode summary */}
          <div className="rounded-lg bg-gray-50 dark:bg-gray-800/50 border px-4 py-3">
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              <SummaryRow label="Drug" value={drugName} />
              <SummaryRow
                label="PGD"
                value={pgdId}
                valueClassName="font-mono text-xs"
              />
              <SummaryRow label="Starting dose" value={initialDose} />
              <SummaryRow label="Episode type" value={episodeLabel} />
              <SummaryRow
                label="Baseline weight"
                value={`${baselineWeightKg} kg`}
                muted="(recorded at assessment)"
              />
              <SummaryRow
                label="Baseline BMI"
                value={baselineBmi.toFixed(1)}
              />
            </div>
          </div>

          {/* Email warning */}
          {emailMissing && (
            <div className="rounded-lg border border-yellow-300 bg-yellow-50 dark:bg-yellow-950/30 dark:border-yellow-800 px-4 py-3 space-y-3">
              <div className="flex items-start gap-2">
                <span className="text-yellow-600 dark:text-yellow-400 text-base leading-none mt-0.5">⚠</span>
                <div>
                  <p className="text-sm font-semibold text-yellow-800 dark:text-yellow-300">No email address on file</p>
                  <p className="text-xs text-yellow-700 dark:text-yellow-400 mt-0.5">
                    Questionnaires cannot be sent automatically without a patient email address.
                  </p>
                </div>
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium text-yellow-800 dark:text-yellow-300" htmlFor="aht-patient-email">
                  Patient email address
                </label>
                <input
                  id="aht-patient-email"
                  type="email"
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  placeholder="email@example.com"
                  className="w-full rounded-md border border-yellow-300 dark:border-yellow-700 bg-white dark:bg-gray-900 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
                />
              </div>
            </div>
          )}

          {/* Appointments */}
          <div className="rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-100 dark:border-blue-900 px-4 py-3">
            <p className="text-sm font-semibold mb-2">
              Appointments created with this plan ({plannedAppointments.length} total)
            </p>
            <div
              className="overflow-y-auto divide-y divide-blue-100 dark:divide-blue-900"
              style={{ maxHeight: "320px" }}
            >
              {plannedAppointments.map((appointment) => (
                <AppointmentRow key={appointment.id} appointment={appointment} />
              ))}
            </div>
          </div>
        </div>

        {saveError && (
          <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-900 dark:bg-red-950/30 dark:text-red-200">
            <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
            <span>{saveError}</span>
          </div>
        )}

        {/* Footer */}
        <div className="flex justify-between gap-3 pt-2">
          <Button
            variant="outline"
            onClick={onCancel}
            disabled={isConfirming}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!canConfirm}
            title={emailMissing && !emailValid ? "Add a patient email address to continue" : undefined}
          >
            {isConfirming && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Confirm and create treatment plan
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
